import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import {
  LayoutDashboard,
  Users,
  FileText,
  Banknote,
  Package,
  ShieldAlert,
  File,
  Settings,
  User,
  MoreVertical,
  ChevronDown,
  X,
} from 'lucide-react';

const Menu = () => {
  const { user, selectedMicrocredit, setSelectedMicrocredit, userRole, userInfo } =
    useAuth();
  const { menuOpen, setMenuOpen } = useData();
  const data = useData();
  const [openDropDown, setOpenDropDown] = useState('');
  const [mobileMenusOpen, setMobileMenusOpen] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const iconColorClass = (active) =>
    active ? 'text-blue-600' : 'text-slate-600 group-hover:text-blue-600';

  function getIcon(icon, active) {
    const base = 'h-5 w-5 shrink-0 transition-colors';
    const color = iconColorClass(active);
    const icons = {
      dashboard: <LayoutDashboard className={`${base} ${color}`} />,
      users: <Users className={`${base} ${color}`} />,
      loans: <FileText className={`${base} ${color}`} />,
      cash_management: <Banknote className={`${base} ${color}`} />,
      inventory: <Package className={`${base} ${color}`} />,
      risk_management: <ShieldAlert className={`${base} ${color}`} />,
      legal: <File className={`${base} ${color}`} />,
      payments: <Banknote className={`${base} ${color}`} />,
      more: <MoreVertical className={`${base} ${color}`} />,
      settings: <Settings className={`${base} ${color}`} />,
      profile: <User className={`${base} ${color}`} />,
      microcredits: <LayoutDashboard className={`${base} ${color}`} />,
    };
    return icons[icon];
  }

  const operator_first_links = {
    users: userInfo?.permissions?.clients?.includes('read')
      ? '/clients'
      : userInfo?.permissions?.operators?.includes('read')
      ? '/operators'
      : null,
    contracts: userInfo?.permissions?.contracts?.includes('read')
      ? '/loans'
      : userInfo?.permissions?.contracts?.includes('create')
      ? '/loans/create'
      : userInfo?.permissions?.contract_settings?.includes('read')
      ? '/loans/settings'
      : userInfo?.permissions?.contract_template?.includes('update')
      ? '/loans/template/contract'
      : userInfo?.permissions?.clearance_certificate_template?.includes('update')
      ? '/loans/template/certificate'
      : userInfo?.permissions?.debt_confession_agreement_template?.includes('update')
      ? '/loans/template/agreement'
      : null,
    cash_management: userInfo?.permissions?.cash_management_transactions?.includes('read')
      ? '/cash-management'
      : userInfo?.permissions?.cash_management_reports?.includes('read')
      ? '/cash-management/stats'
      : userInfo?.permissions?.cash_management_settings?.includes('read')
      ? '/cash-management/settings'
      : null,
    inventory: userInfo?.permissions?.inventory?.includes('read')
      ? '/inventory'
      : userInfo?.permissions?.inventory_settings?.includes('read')
      ? '/inventory/settings'
      : null,
    risk_management: userInfo?.permissions?.risk_management?.includes('read')
      ? '/risk-management/clients'
      : userInfo?.permissions?.blacklist?.includes('read')
      ? '/risk-management/blacklist'
      : userInfo?.permissions?.risk_management_settings?.includes('read')
      ? '/risk-management/settings'
      : null,
    legal_guidelines: userInfo?.permissions?.legal_guidelines?.includes('read')
      ? '/legal/contracts'
      : null,
    payments: userInfo?.permissions?.payments?.includes('read') ? '/payments' : null,
  };

  /**
   * Returns true if the given path pattern matches the current pathname.
   * Handles:
   *  - exact matches           (/loans  === /loans)
   *  - dynamic segments        (loan/:id  vs  loan/123)
   *  - nested/child paths      (/loans/template  vs  /loans/template/contract)
   *  - home route              ('/dashboard' or '/')
   */
  const pathMatches = (pattern) => {
    if (!pattern) return false;

    const cleanPath = pathname.split('?')[0].split('#')[0];

    // Normalize: strip leading slash for comparison, but keep a leading "/" marker
    const normPattern = pattern.startsWith('/') ? pattern : `/${pattern}`;
    const normPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;

    // Root / dashboard special case
    if (normPattern === '/dashboard' && (normPath === '/' || normPath === '/dashboard')) {
      return true;
    }

    const patternParts = normPattern.split('/').filter(Boolean);
    const pathParts = normPath.split('/').filter(Boolean);

    // If pattern has more segments than the current path, it can't match
    // (unless it's a prefix, handled below)
    if (patternParts.length > pathParts.length) return false;

    for (let i = 0; i < patternParts.length; i++) {
      const p = patternParts[i];

      // Dynamic segment like ":id"
      if (p.startsWith(':')) continue;

      // Exact segment mismatch → no match
      if (p !== pathParts[i]) return false;
    }

    // All pattern segments matched. Match also when pathname is deeper
    // (so /loans/template matches /loans/template/contract).
    return true;
  };

  const checkActive = (item, isSub) => {
    if (!item) return false;

    if (isSub || !item.sub_menus) {
      return (item.paths || []).some((p) => pathMatches(p));
    }

    // Parent with submenus: active if any submenu is active
    return item.sub_menus.some((sub) => (sub.paths || []).some((p) => pathMatches(p)));
  };

  // Toggle only the dropdown, no navigation here
  function toggleDropdown(field) {
    setOpenDropDown((prev) => (prev === field ? '' : field));
  }

  // Keep the correct dropdown open on route change
  useEffect(() => {
    const matched = menuItems.find((item) => {
      if (item.sub_menus) {
        return item.sub_menus.some((sub) =>
          (sub.paths || []).some((p) => pathMatches(p))
        );
      }
      return (item.paths || []).some((p) => pathMatches(p));
    });
    if (matched?.sub_menus) {
      setOpenDropDown(matched.field);
    } else {
      setOpenDropDown('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  function go_to(path, hasSubmenus) {
    data.currentPage = path;
    navigate(path);
    if (!hasSubmenus) setMenuOpen(false);
  }

  function closeAndOpen(path) {
    if (mobileMenusOpen.includes(path)) {
      setMobileMenusOpen(mobileMenusOpen.filter((i) => i != path));
    } else {
      data._showPopUp('mobile_menu');
      setMobileMenusOpen([path]);
    }
  }

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      paths: ['/dashboard'],
      field: 'dashboard',
      icon: 'dashboard',
      show: !(userRole == 'operator' && !userInfo?.permissions?.dashboard?.includes('read')),
    },
    {
      name: 'Microcreditos',
      path: '/microcredits',
      paths: ['/microcredits'],
      field: 'microcredits',
      icon: 'microcredits',
      show: userRole == 'admin',
    },
    {
      name: 'Utilizadores',
      path: '/clients',
      paths: ['/clients', '/operators', '/managers', '/subadmins'],
      field: 'users',
      icon: 'users',
      sub_menus: [
        { name: 'Clientes', path: '/clients', paths: ['/clients'], show: true },
        { name: 'Operadores', path: '/operators', paths: ['/operators'], show: userRole != 'operator' },
        { name: 'Gestores', path: '/managers', paths: ['/managers'], show: userRole == 'admin' },
        { name: 'Sub Admins', path: '/subadmins', paths: ['/subadmins'], show: userRole == 'admin' },
      ],
      show: userRole != 'client',
    },
    {
      name: 'Contratos',
      path: '/loans',
      paths: [
        '/loans',
        '/loans/create',
        '/loans/:id',
        '/loans/template',
        '/loans/template/contract',
        '/loans/template/agreement',
        '/loans/template/certificate',
        '/loans/settings',
      ],
      field: 'loans',
      icon: 'loans',
      sub_menus: [
        { name: 'Contratos', path: '/loans', paths: ['/loans'], show: true },
        { name: 'Criar', path: '/loans/create', paths: ['/loans/create'], show: true },
        { name: 'Definições', path: '/loans/settings', paths: ['/loans/settings'], show: userRole != 'operator' },
        {
          name: 'Modelo',
          path: '/loans/template/contract',
          paths: ['/loans/template', '/loans/template/contract', '/loans/template/agreement', '/loans/template/certificate'],
          show: userRole != 'client',
        },
      ],
      show: userRole != 'client' && userRole != 'admin',
    },
    {
      name: 'Caixa',
      path: '/cash-management',
      paths: ['/cash-management', '/cash-management/settings', '/cash-management/stats', '/cash-management/bank-stats'],
      field: 'cash_management',
      icon: 'cash_management',
      sub_menus: [
        { name: 'Caixa', path: '/cash-management', paths: ['/cash-management'], show: true },
        { name: 'Relatórios', path: '/cash-management/stats', paths: ['/cash-management/stats', '/cash-management/bank-stats'], show: true },
        { name: 'Definições', path: '/cash-management/settings', paths: ['/cash-management/settings'], show: userRole != 'operator' },
      ],
      show: userRole != 'client' && userRole != 'admin',
    },
    {
      name: 'Inventário',
      path: '/inventory',
      paths: ['/inventory', '/inventory/settings'],
      field: 'inventory',
      icon: 'inventory',
      sub_menus: [
        { name: 'Bens', path: '/inventory', paths: ['/inventory'], show: true },
        { name: 'Definições', path: '/inventory/settings', paths: ['/inventory/settings'], show: userRole != 'operator' },
      ],
      show:
        (userRole == 'manager' || operator_first_links?.inventory) &&
        (user?.microcredits?.filter((i) => i.id == selectedMicrocredit)[0]?.plan == 'gold' ||
          user?.microcredits?.filter((i) => i.id == selectedMicrocredit)[0]?.plan == 'premium' ||
          userRole == 'admin'),
    },
    {
      name: 'Gestão de risco',
      path: '/risk-management/clients',
      paths: [
        '/risk-management/clients',
        '/risk-management/blacklist',
        '/risk-management/find-external-clients',
        '/risk-management/settings',
      ],
      field: 'risk_management',
      icon: 'risk_management',
      sub_menus: [
        {
          name: userRole == 'admin' ? 'Clientes' : 'Meus clientes',
          path: '/risk-management/clients',
          paths: ['/risk-management/clients'],
          show: true,
        },
        {
          name: 'Consulta externa de clientes',
          path: '/risk-management/find-external-clients',
          paths: ['/risk-management/find-external-clients'],
          show: userRole != 'operator' || userInfo?.permissions?.search_external_clients?.includes('read'),
        },
        {
          name: 'Blacklist',
          path: '/risk-management/blacklist',
          paths: ['/risk-management/blacklist'],
          show: userRole != 'operator' || userInfo?.permissions?.blacklist?.includes('read'),
        },
        {
          name: 'Definições',
          path: '/risk-management/settings',
          paths: ['/risk-management/settings'],
          show:
            userRole == 'manager' ||
            (userRole == 'operator' && userInfo?.permissions?.risk_management_settings?.includes('read')),
        },
      ],
      show:
        ((userRole == 'manager' || operator_first_links?.risk_management) &&
          (user?.microcredits?.filter((i) => i.id == selectedMicrocredit)[0]?.plan == 'gold' ||
            user?.microcredits?.filter((i) => i.id == selectedMicrocredit)[0]?.plan == 'premium')) ||
        userRole == 'admin',
    },
    {
      name: 'Jurídico',
      path: '/legal/contracts',
      paths: ['/legal/contracts', '/legal/clients'],
      field: 'legal_guidelines',
      icon: 'legal',
      sub_menus: [
        { name: 'Contratos', path: '/legal/contracts', paths: ['/legal/contracts'], show: true },
        { name: 'Clientes', path: '/legal/clients', paths: ['/legal/clients'], show: true },
      ],
      show: userRole == 'manager' || operator_first_links?.legal_guidelines,
    },
    {
      name: 'Pagamentos e planos',
      path: '/payments',
      paths: ['/payments', '/plans'],
      field: 'payments',
      icon: 'payments',
      sub_menus: [
        { name: 'Pagamentos', path: '/payments', paths: ['/payments'], show: true },
        {
          name: 'Planos',
          path: '/plans',
          paths: ['/plans'],
          show: userRole == 'admin' && user?.admin_level == 'platform_owner',
        },
      ],
      show:
        user?.email != 'test@visum.co.mz' &&
        (userRole == 'admin' || userRole == 'manager' || operator_first_links?.payments),
    },
    {
      name: 'Configurações',
      path: '/settings',
      paths: ['/settings'],
      field: 'settings',
      icon: 'settings',
      show: true,
    },
    {
      name: 'Perfil de usuário',
      path: '/profile',
      paths: ['/profile'],
      field: 'profile',
      icon: 'profile',
      show: true,
    },
  ];

  // Mobile bottom menu: only show a few items, and use "Mais" for the rest
  const mobilePrimaryFields = ['dashboard', 'users', 'loans'];
  const mobilePrimaryItems = menuItems.filter(
    (i) => i.show && mobilePrimaryFields.includes(i.field)
  );
  const mobileMenuItems = [
    ...mobilePrimaryItems,
    {
      name: 'Mais',
      path: '/more',
      paths: ['/more'],
      field: 'more',
      icon: 'more',
      show: true,
    },
  ];

  return (
    <>
      {/* Mobile bottom menu */}
      <div className="fixed -bottom-2 left-0 right-0 z-[999] flex items-center rounded-t-[15px] border-t border-slate-200 bg-white shadow-sm md:hidden">
        <div className="relative flex w-full items-center justify-around px-2 py-4">
          {mobileMenuItems.map((item, index) => {
            const isActive = checkActive(item);
            const showDropdown = item.sub_menus && mobileMenusOpen.includes(item.path);
            return (
              <div key={index}>
                {item.sub_menus && (
                  <div
                    className={`absolute bottom-[100%] left-0 w-full rounded-t-[15px] border-t border-slate-200 bg-white shadow-lg ${
                      showDropdown ? '' : 'hidden'
                    }`}
                  >
                    <div className="flex flex-col rounded-[0.3rem] bg-gray-100 p-[10px]">
                      {item.sub_menus
                        .filter((i) => i.show)
                        .map((sub, subIndex) => (
                          <span
                            key={subIndex}
                            onClick={() => {
                              setMenuOpen(false);
                              setMobileMenusOpen([]);
                              navigate(sub.path);
                            }}
                            className={`flex cursor-pointer py-2 text-[1rem] ${
                              checkActive(sub, true)
                                ? 'font-medium text-blue-700'
                                : 'text-slate-600 opacity-80 hover:text-blue-700'
                            }`}
                          >
                            {sub.name}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                <div
                  onClick={() => {
                    data._closeAllPopUps();
                    setMobileMenusOpen([]);
                    if (item.field == 'more') {
                      setMenuOpen(true);
                      return;
                    }
                    setMenuOpen(false);
                    if (item.sub_menus) {
                      closeAndOpen(item.path);
                    } else {
                      navigate(item.path);
                    }
                  }}
                  className="flex cursor-pointer flex-col items-center"
                >
                  {getIcon(item.icon, (isActive && !menuOpen) || (menuOpen && item.field == 'more'))}
                  <span
                    className={`text-[0.8rem] ${
                      (isActive && !menuOpen) || (menuOpen && item.field == 'more')
                        ? 'font-medium text-blue-700'
                        : 'text-slate-500'
                    }`}
                  >
                    {item.name}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile "Mais" full menu overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-[1000] flex flex-col bg-white md:hidden">
          <div className="flex items-center justify-between border-b border-slate-200 p-4">
            <span className="text-lg font-semibold text-slate-800">Menu</span>
            <div
              className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"
              onClick={() => setMenuOpen(false)}
            >
              <X className="h-5 w-5" />
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-1 overflow-y-auto p-4">
            {menuItems
              .filter((i) => i.show)
              .map((item, index) => {
                const isActive = checkActive(item);
                const isOpen = openDropDown == item.field;
                const showDropdown = item.sub_menus && isOpen;
                return (
                  <div key={index}>
                    <div
                      onClick={() => {
                        if (item.sub_menus) {
                          toggleDropdown(item.field);
                        } else {
                          setMenuOpen(false);
                          go_to(item.path);
                        }
                      }}
                      className={`group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-slate-700 hover:bg-slate-50 hover:text-blue-700'
                      }`}
                    >
                      {getIcon(item.icon, isActive)}
                      <span className="flex-1">{item.name}</span>
                      {item.sub_menus && (
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      )}
                    </div>
                    {item.sub_menus && (
                      <div
                        className={`ml-6 mt-1 flex-col gap-0.5 border-l-2 border-slate-100 pl-4 ${
                          showDropdown ? 'flex' : 'hidden'
                        }`}
                      >
                        {item.sub_menus
                          .filter((i) => i.show)
                          .map((sub, subIndex) => {
                            const subActive = checkActive(sub, true);
                            return (
                              <span
                                key={subIndex}
                                onClick={() => {
                                  setMenuOpen(false);
                                  go_to(sub.path);
                                }}
                                className={`cursor-pointer rounded-md px-3 py-1.5 text-sm ${
                                  subActive ? 'text-blue-700' : 'text-slate-600 hover:text-blue-700'
                                }`}
                              >
                                {sub.name}
                              </span>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div
        className={`absolute inset-y-0 left-0 z-40 flex w-64 flex-col gap-2 overflow-y-auto bg-white p-4 transition-transform duration-200
                   md:static md:inset-auto md:z-auto md:translate-x-0
                   ${menuOpen ? 'translate-x-0' : '-translate-x-full'}
                   shadow-lg ring-1 ring-slate-200/50 md:shadow-none md:ring-0`}
      >
        <div className="flex flex-col gap-1">
          {user?.microcredits?.filter((i) => i.id == selectedMicrocredit)?.[0]?.logo_filename && (
            <div className="mb-4 flex justify-center">
              <img
                className="h-12 w-12 object-cover"
                src={`${process.env.REACT_APP_BASE_URL}/uploaded_files/` + user.microcredits?.filter((i) => i.id == selectedMicrocredit)?.[0]?.logo_filename}
                alt="logo"
              />
            </div>
          )}
          {user?.role != 'admin' && user?.microcredits.length == 1 && (
            <h1 className="text-center text-lg font-semibold text-slate-800">
              {user?.microcredits[0]?.name}
            </h1>
          )}
          {user?.role != 'admin' && user?.microcredits.length != 1 && (
            <select
              onChange={(e) => {
                navigate('/dashboard');
                setSelectedMicrocredit(e.target.value);
                setMenuOpen(false);
              }}
              value={selectedMicrocredit}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none"
            >
              {user?.microcredits?.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          )}
          {user?.role == 'admin' && (
            <h1 className="text-center text-lg font-semibold text-slate-800">VLMS</h1>
          )}
          <div className="w-full text-center">
            <span className="text-xs font-normal text-slate-500">
              {userRole == 'admin'
                ? 'Admin'
                : userRole == 'manager'
                ? 'Gestor'
                : userRole == 'operator'
                ? 'Operador'
                : 'Cliente'}
            </span>
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-1">
          {menuItems
            .filter((i) => i.show)
            .map((item, index) => {
              const isActive = checkActive(item);
              const isOpen = openDropDown == item.field;
              const showDropdown = item.sub_menus && isOpen;
              return (
                <div key={index}>
                  <div
                    onClick={() => {
                      if (item.sub_menus) {
                        // Just toggle the dropdown — do NOT navigate away
                        toggleDropdown(item.field);
                      } else {
                        go_to(item.path);
                      }
                    }}
                    className={`group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-blue-700'
                    }`}
                  >
                    {getIcon(item.icon, isActive)}
                    <span className="flex-1">{item.name}</span>
                    {item.sub_menus && (
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    )}
                  </div>
                  {item.sub_menus && (
                    <div
                      className={`ml-6 mt-1 flex-col gap-0.5 border-l-2 border-slate-100 pl-4 ${
                        showDropdown ? 'flex' : 'hidden'
                      }`}
                    >
                      {item.sub_menus
                        .filter((i) => i.show)
                        .map((sub, subIndex) => {
                          const subActive = checkActive(sub, true);
                          return (
                            <span
                              key={subIndex}
                              onClick={() => go_to(sub.path)}
                              className={`cursor-pointer rounded-md px-3 py-1.5 text-sm ${
                                subActive ? 'text-blue-700' : 'text-slate-600 hover:text-blue-700'
                              }`}
                            >
                              {sub.name}
                            </span>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
};

export default Menu;